import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { getCurationProducts } from '../services/catalog-operations.service';
import type { ProductFilterValues } from '../types/catalog-operations.types';

interface UseCurationProductsParams {
  /** Curation run uuid carried over from the jobs listing payload. The hook
   *  is gated on this — pass `undefined` when not yet known and the query
   *  stays disabled. */
  curationRefId: string | undefined;
  /** UI batch identifier threaded into each mapped product. */
  batchId: string;
  /** 1-based page index. */
  page?: number;
  /** Page size. */
  size?: number;
  /** Filter values keyed by the backend filter key. Includes `needs_attention`
   *  when the Needs Attention tab is active; merged with any drawer-applied
   *  filters at the call site. */
  filters?: ProductFilterValues;
  /** Free-text search committed by the user (Enter). */
  search?: string;
  /** Sort column (`title` | `sku` | `brand` | `category`); `null` → backend default. */
  sortBy?: string | null;
  /** Sort direction; defaults to `asc`. */
  sortDir?: 'asc' | 'desc';
}

/** Fetches a paginated page of curation products. Disabled until
 *  `curationRefId` is known so consumers can mount the hook unconditionally
 *  while the parent resolves the id. The `filters` object and `search`
 *  string are part of the query key so each permutation slots its own
 *  cache entry. */
export function useCurationProducts({
  curationRefId,
  batchId,
  page = 1,
  size = 20,
  filters,
  search,
  sortBy,
  sortDir,
}: UseCurationProductsParams) {
  return useQuery({
    queryKey: queryKeys.curation.runProducts(
      curationRefId ?? '',
      page,
      size,
      filters ?? {},
      search ?? '',
      sortBy ?? null,
      sortDir ?? 'asc',
    ),
    queryFn: () =>
      getCurationProducts({
        curationRefId: curationRefId ?? '',
        batchId,
        page,
        size,
        ...(filters ? { filters } : {}),
        ...(search ? { search } : {}),
        ...(sortBy ? { sortBy, sortDir: sortDir ?? 'asc' } : {}),
      }),
    enabled: Boolean(curationRefId),
    placeholderData: keepPreviousData,
  });
}
